import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, Loader, CheckCircle, Video, Phone, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { user } = useAuth();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [ev, sess] = await Promise.all([
          axios.get(`${API}/events`, { withCredentials: true }),
          axios.get(`${API}/sessions/mine`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setEvents(ev.data || []);
        // Only confirmed sessions (paid) appear as calendar entries
        setMySessions((sess.data || []).filter(s => s.status === "confirmed"));
      } catch (err) {
        console.error("Error loading events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const eventDates = events.map(e => {
    const parts = e.date.split("-");
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  });
  const sessionDates = mySessions.map(s => {
    const parts = s.date.split("-");
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  });

  const selectedDateStr = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
    : "";
  const filteredEvents = events.filter(e => e.date === selectedDateStr);
  const filteredSessions = mySessions.filter(s => s.date === selectedDateStr);

  const allEventsThisMonth = events.filter(e => {
    const parts = e.date.split("-");
    return parseInt(parts[0]) === currentMonth.getFullYear() && parseInt(parts[1]) - 1 === currentMonth.getMonth();
  });

  const handleRegister = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/register`, {}, { withCredentials: true });
      toast.success("Inscrito correctamente");
      const { data } = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al inscribirse");
    }
  };

  const handleUnregister = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/unregister`, {}, { withCredentials: true });
      toast.success("Desinscrito correctamente");
      const { data } = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(data);
    } catch {
      toast.error("Error al desinscribirse");
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long" });
  };

  return (
    <div data-testid="events-page" className="max-w-lg mx-auto px-4 py-6">
      <h1 className="font-heading text-2xl text-[#5B3E2E] mb-4">Eventos</h1>

      {/* Calendar - Mobile optimized */}
      <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-3 sm:p-4 mb-6">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => d && setSelectedDate(d)}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={es}
          modifiers={{ event: eventDates, session: sessionDates }}
          modifiersClassNames={{
            event: "bg-[#E48D57]/20 text-[#E48D57] font-semibold rounded-full",
            session: "ring-2 ring-[#6B9070] ring-offset-1 ring-offset-[#FDFAF4] rounded-full",
          }}
          className="mx-auto [&_.rdp-caption]:px-0 [&_.rdp-cell]:p-0 [&_.rdp-day]:h-9 [&_.rdp-day]:w-9 [&_.rdp-head_cell]:w-9 [&_.rdp-table]:w-full"
          classNames={{
            months: "flex flex-col",
            month: "space-y-2",
            caption: "flex justify-between items-center px-2",
            caption_label: "text-base font-heading font-semibold text-[#5B3E2E]",
            nav: "flex items-center gap-1",
            nav_button: "h-8 w-8 bg-transparent p-0 text-[#5B3E2E]/60 hover:text-[#5B3E2E] rounded-full hover:bg-[#5B3E2E]/5",
            table: "w-full border-collapse",
            head_row: "flex justify-around",
            head_cell: "text-[#5B3E2E]/40 text-xs font-medium w-9 text-center",
            row: "flex justify-around mt-1",
            cell: "text-center p-0",
            day: "h-9 w-9 text-sm font-medium rounded-full hover:bg-[#5B3E2E]/5 text-[#5B3E2E] transition-colors flex items-center justify-center",
            day_selected: "bg-[#6B9070] text-white hover:bg-[#6B9070]",
            day_today: "font-bold text-[#E48D57]",
            day_outside: "text-[#5B3E2E]/20",
          }}
        />
      </div>

      {/* Events for selected date */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg text-[#5B3E2E]">
          {selectedDate ? `Eventos del ${selectedDate.getDate()}` : "Selecciona un dia"}
        </h2>
        {(filteredEvents.length + filteredSessions.length) > 0 && (
          <span className="text-xs text-[#5B3E2E]/40 bg-[#5B3E2E]/5 px-2 py-1 rounded-full">{filteredEvents.length + filteredSessions.length}</span>
        )}
      </div>

      {/* My confirmed 1:1 sessions on this date */}
      {filteredSessions.length > 0 && (
        <div className="space-y-3 mb-4">
          {filteredSessions.map(s => (
            <div key={s.id} data-testid={`session-card-${s.id}`} className="bg-[#6B9070]/8 rounded-3xl border border-[#6B9070]/25 overflow-hidden p-4 sm:p-5 animate-slide-up">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-[#6B9070]/15 flex items-center justify-center flex-shrink-0">
                    <HeartHandshake size={16} className="text-[#6B9070]" />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B9070] font-semibold">Sesión 1:1 · Confirmada</p>
                    <p className="font-heading text-lg text-[#5B3E2E] font-semibold">Acompañamiento individualizado</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-[#5B3E2E]/60 mt-2">
                <div className="flex items-center gap-1.5">
                  <Clock size={13} /><span>{s.time} · 30 min</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {s.modality === "video" ? <><Video size={13} /><span>Llamada de WhatsApp</span></> : <><Phone size={13} /><span>Llamada de teléfono</span></>}
                </div>
              </div>
              <p className="text-[11px] text-[#5B3E2E]/50 mt-3">Te contactaré al WhatsApp <strong>{s.phone}</strong> a la hora indicada.</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader size={24} className="text-[#E48D57] animate-spin" />
        </div>
      ) : filteredEvents.length === 0 ? (
        filteredSessions.length === 0 ? (
          <div className="text-center py-8 bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10">
            <CalendarDays size={28} className="text-[#5B3E2E]/15 mx-auto mb-2" />
            <p className="text-sm text-[#5B3E2E]/40">No hay eventos para este día</p>
          </div>
        ) : null
      ) : (
        <div className="space-y-4">
          {filteredEvents.map((event) => {
            const isRegistered = event.attendees?.includes(user?.id);
            return (
              <div key={event.id} data-testid={`event-card-${event.id}`} className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 overflow-hidden animate-slide-up">
                {event.image_url && (
                  <div className="relative h-36 sm:h-44">
                    <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#5B3E2E]/70 to-transparent" />
                    <h3 className="absolute bottom-3 left-4 font-heading text-lg text-white font-semibold">{event.title}</h3>
                  </div>
                )}
                <div className="p-4 sm:p-5">
                  {!event.image_url && <h3 className="font-heading text-lg text-[#5B3E2E] font-semibold mb-2">{event.title}</h3>}
                  <p className="text-sm text-[#5B3E2E]/70 mb-3 line-clamp-2">{event.description}</p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-[#5B3E2E]/50 mb-4">
                    <div className="flex items-center gap-1.5">
                      <CalendarDays size={13} />
                      <span>{formatDate(event.date)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock size={13} />
                      <span>{event.time}</span>
                    </div>
                    {event.attendees?.length > 0 && (
                      <span className="bg-[#6B9070]/10 text-[#6B9070] px-2 py-0.5 rounded-full">{event.attendees.length} inscritos</span>
                    )}
                  </div>
                  {isRegistered ? (
                    <div className="flex flex-wrap gap-2">
                      {event.streaming_url && (
                        <a href={event.streaming_url} target="_blank" rel="noopener noreferrer" data-testid={`streaming-link-${event.id}`}>
                          <Button className="rounded-full bg-[#6B9070] hover:bg-[#5A8060] text-white text-sm flex items-center gap-1.5">
                            <Video size={14} />
                            Unirse al streaming
                          </Button>
                        </a>
                      )}
                      <Button data-testid={`unregister-event-${event.id}`} onClick={() => handleUnregister(event.id)} variant="outline" className="rounded-full border-[#5B3E2E]/20 text-[#5B3E2E]/60 hover:bg-[#5B3E2E]/5 flex items-center gap-1.5 text-sm">
                        <CheckCircle size={14} />
                        Cancelar inscripción
                      </Button>
                    </div>
                  ) : (
                    <Button data-testid={`register-event-${event.id}`} onClick={() => handleRegister(event.id)} className="w-full sm:w-auto rounded-full bg-[#E48D57] hover:bg-[#D07C46] text-white text-sm">
                      Inscribirse
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upcoming events this month */}
      {allEventsThisMonth.length > 0 && filteredEvents.length === 0 && (
        <div className="mt-6">
          <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3">Próximos eventos este mes</p>
          <div className="space-y-2">
            {allEventsThisMonth.slice(0, 5).map((event) => (
              <button
                key={event.id}
                onClick={() => {
                  const parts = event.date.split("-");
                  setSelectedDate(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
                }}
                className="w-full text-left p-3 bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 hover:bg-[#F0E7D3] transition-colors"
              >
                <p className="text-sm font-medium text-[#5B3E2E]">{event.title}</p>
                <p className="text-xs text-[#5B3E2E]/50 mt-0.5">{formatDate(event.date)} - {event.time}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
